import React, { useCallback } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { useWatch } from "antd/es/form/Form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import { useIntl } from "react-intl";

import Organization from "@mapmycustomers/shared/types/Organization";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import DuplicateMergeAccessValue from "@app/enum/DuplicateMergeAccessValue";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import DataSettings from "@app/scene/settings/type/DataSettings";
import showSaveSettingSuccessfulMessage from "@app/scene/settings/util/showSaveSettingSuccessfulMessage";
import {
  doesOrganizationSupportHipaa,
  getOrganizationId,
  getOrganizationSettings,
  isSsoEnabled,
} from "@app/store/iam";
import { updateOrganization } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import useSetting from "@app/util/hook/useSetting";

import AutomaticSsoSignIn from "./components/AutomaticSsoSignIn";
import DuplicateMergeAccess from "./components/DuplicateMergeAccess";
import DuplicateRecords from "./components/DuplicateRecords";
import ExportUid from "./components/ExportUid";
import ImportRestriction from "./components/ImportRestriction";
import SsoAlert from "./components/SsoAlert";
import SupportAccess from "./components/SupportAccess";
import styles from "./DataSettings.module.scss";

interface Props {
  form: FormInstance<DataSettings>;
  isSsoEnabled: boolean;
  onHasChanges: () => void;
  onSaved: () => void;
  onUpdateOrganization: typeof updateOrganization.request;
  organizationId?: number;
  organizationSettings: Organization["settings"];
  supportsHipaa: boolean;
}

const Data: React.FC<Props> = ({
  form,
  isSsoEnabled,
  onHasChanges,
  onSaved,
  onUpdateOrganization,
  organizationId,
  organizationSettings,
  supportsHipaa,
}) => {
  const intl = useIntl();
  const allowPhiAccessSetting = useSetting(
    organizationSettings,
    OrganizationSetting.ALLOW_PHI_ACCESS_TO_MMC
  );

  const duplicateCheckSetting = useSetting(
    organizationSettings,
    OrganizationSetting.DUPLICATE_CHECK
  );

  const duplicateMergeAccessSetting = useSetting(
    organizationSettings,
    OrganizationSetting.DUPLICATE_MERGE_ACCESS
  );

  const exportSetting = useSetting(organizationSettings, OrganizationSetting.EXPORT_UID);

  const importAllowed = useSetting(organizationSettings, OrganizationSetting.IMPORT_ALLOWED);

  const ssoLoginApprovalRequiredSetting = useSetting(
    organizationSettings,
    OrganizationSetting.SSO_LOGIN_APPROVAL_REQUIRED
  );

  const duplicateMergeAccess = useWatch(OrganizationSetting.DUPLICATE_MERGE_ACCESS, form);

  const handleSubmit = useCallback(() => {
    const formValues = form.getFieldsValue();

    if (organizationId) {
      onUpdateOrganization({
        callback: () => {
          showSaveSettingSuccessfulMessage(intl);
          onSaved();
        },
        organization: { id: organizationId },
        settings: [
          allowPhiAccessSetting &&
          isDefined(formValues[OrganizationSetting.ALLOW_PHI_ACCESS_TO_MMC])
            ? {
                ...allowPhiAccessSetting,
                value: formValues[OrganizationSetting.ALLOW_PHI_ACCESS_TO_MMC],
              }
            : undefined,
          duplicateCheckSetting && formValues[OrganizationSetting.DUPLICATE_CHECK]
            ? { ...duplicateCheckSetting, value: formValues[OrganizationSetting.DUPLICATE_CHECK] }
            : undefined,
          duplicateMergeAccessSetting && formValues[OrganizationSetting.DUPLICATE_MERGE_ACCESS]
            ? {
                ...duplicateMergeAccessSetting,
                value: formValues[OrganizationSetting.DUPLICATE_MERGE_ACCESS],
              }
            : undefined,
          exportSetting && isDefined(formValues[OrganizationSetting.EXPORT_UID])
            ? { ...exportSetting, value: formValues[OrganizationSetting.EXPORT_UID] }
            : undefined,
          ssoLoginApprovalRequiredSetting &&
          isDefined(formValues[OrganizationSetting.SSO_LOGIN_APPROVAL_REQUIRED])
            ? {
                ...ssoLoginApprovalRequiredSetting,
                value: !formValues[OrganizationSetting.SSO_LOGIN_APPROVAL_REQUIRED],
              }
            : undefined,
          importAllowed && isDefined(formValues[OrganizationSetting.IMPORT_ALLOWED])
            ? {
                ...importAllowed,
                value: formValues[OrganizationSetting.IMPORT_ALLOWED],
              }
            : undefined,
        ].filter(isDefined),
      });
    }
  }, [
    allowPhiAccessSetting,
    duplicateCheckSetting,
    duplicateMergeAccessSetting,
    exportSetting,
    form,
    importAllowed,
    intl,
    onSaved,
    onUpdateOrganization,
    organizationId,
    ssoLoginApprovalRequiredSetting,
  ]);

  return (
    <Form
      className={styles.container}
      form={form}
      initialValues={{
        [OrganizationSetting.ALLOW_PHI_ACCESS_TO_MMC]: allowPhiAccessSetting?.value,
        [OrganizationSetting.DUPLICATE_CHECK]: duplicateCheckSetting?.value,
        [OrganizationSetting.DUPLICATE_MERGE_ACCESS]: duplicateMergeAccessSetting?.value,
        [OrganizationSetting.EXPORT_UID]: exportSetting?.value,
        [OrganizationSetting.IMPORT_ALLOWED]: importAllowed?.value,
        [OrganizationSetting.SSO_LOGIN_APPROVAL_REQUIRED]: !ssoLoginApprovalRequiredSetting?.value,
      }}
      onFieldsChange={onHasChanges}
      onFinish={handleSubmit}
    >
      {isSsoEnabled ? (
        <Form.Item name={OrganizationSetting.SSO_LOGIN_APPROVAL_REQUIRED}>
          <AutomaticSsoSignIn />
        </Form.Item>
      ) : (
        <SsoAlert />
      )}
      {supportsHipaa && (
        <Form.Item name={OrganizationSetting.ALLOW_PHI_ACCESS_TO_MMC}>
          <SupportAccess />
        </Form.Item>
      )}
      <Form.Item name={OrganizationSetting.EXPORT_UID}>
        <ExportUid />
      </Form.Item>
      <div>
        <Form.Item name={OrganizationSetting.DUPLICATE_MERGE_ACCESS}>
          <DuplicateMergeAccess />
        </Form.Item>
        {duplicateMergeAccess !== DuplicateMergeAccessValue.OWNER && (
          <Form.Item name={OrganizationSetting.DUPLICATE_CHECK}>
            <DuplicateRecords />
          </Form.Item>
        )}
      </div>
      <Form.Item name={OrganizationSetting.IMPORT_ALLOWED}>
        <ImportRestriction />
      </Form.Item>
    </Form>
  );
};

const mapStateToProps = (state: RootState) => ({
  isSsoEnabled: isSsoEnabled(state),
  organizationId: getOrganizationId(state),
  organizationSettings: getOrganizationSettings(state) ?? [],
  supportsHipaa: doesOrganizationSupportHipaa(state),
});

const mapDispatchToProps = {
  onUpdateOrganization: updateOrganization.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Data);
